
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PaymentInfo } from '@/hooks/useCourierPayments';
import { formatDate } from '@/utils/dateUtils';
import { ExternalLink, Loader2 } from 'lucide-react';
import { PaymentStatusBadge } from '@/pages/admin/payments/PaymentStatusBadge';
import { toast } from '@/components/ui/sonner';
import { getPaymentUrl } from '../hooks/usePendingPayments';
import { useIsMobile } from '@/hooks/use-mobile';

interface PendingPaymentsCardProps {
  pendingPayments: PaymentInfo[];
}

export const PendingPaymentsCard: React.FC<PendingPaymentsCardProps> = ({ pendingPayments }) => {
  const [loadingPaymentId, setLoadingPaymentId] = useState<string | null>(null);
  const isMobile = useIsMobile();

  if (!pendingPayments || pendingPayments.length === 0) return null;

  // Обработчик для перехода на страницу оплаты
  const handlePaymentRedirect = async (payment: PaymentInfo) => {
    try {
      // Устанавливаем состояние загрузки
      setLoadingPaymentId(payment.id);
      
      // Показываем уведомление перед переходом
      toast.info('Подготовка страницы оплаты...', {
        description: 'Загрузка страницы',
        duration: 3000
      });
      
      // Для платежей в ожидании с ссылкой receiptUrl
      if (payment.receiptUrl) {
        window.location.href = payment.receiptUrl;
        return;
      }
      
      // Создаем URL для возврата после оплаты - изменяем на новый маршрут status
      let baseReturnUrl = `${window.location.origin}/payment/auth/status?payment_id=${payment.id}`;
      
      // Добавляем параметр subscription_id если он есть
      if (payment.subscriptionId) {
        baseReturnUrl += `&subscription_id=${payment.subscriptionId}`;
      }
      
      const paymentUrl = getPaymentUrl(payment.human_number, baseReturnUrl);
      if (paymentUrl) {
        window.location.href = paymentUrl;
      } else {
        toast.error('Не удалось сформировать ссылку на оплату');
      }
      
      setLoadingPaymentId(null);
    } catch (error) {
      toast.error('Ошибка при переходе на страницу оплаты');
      console.error('Ошибка при переходе на страницу оплаты:', error);
      setLoadingPaymentId(null);
    }
  };

  return (
    <Card className="w-full bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-950/20 dark:to-rose-900/20 border-red-200 dark:border-red-800 shadow-md">
      <CardContent className="space-y-4 p-5">
        <h3 className="text-lg font-medium text-center mb-4">Требуется оплата</h3>
        <div className="space-y-3">
          {pendingPayments.map(payment => (
            <div 
              key={`pending-${payment.id}`} 
              className={`
                flex ${isMobile ? 'flex-col' : 'justify-between items-center'}
                bg-white dark:bg-black/20 p-3 rounded-lg border border-red-100 dark:border-red-800
              `}
            >
              <div className={`flex ${isMobile ? 'justify-between items-center mb-3' : 'flex-col'}`}>
                <p className="text-sm font-medium">{payment.amount.toLocaleString()} ₽</p>
                <div className={`${isMobile ? 'text-right' : 'mt-1'}`}>
                  <p className="text-xs text-muted-foreground">{formatDate(payment.date)}</p>
                  <div className={isMobile ? 'mt-1' : 'mt-1'}>
                    <PaymentStatusBadge status={payment.status} className="text-xs" />
                  </div>
                </div>
              </div>
              <Button 
                variant="destructive"
                size="sm"
                onClick={() => handlePaymentRedirect(payment)}
                className={`
                  whitespace-nowrap shadow-sm hover:shadow-md transition-all
                  ${isMobile ? 'w-full' : ''}
                `}
                disabled={loadingPaymentId === payment.id}
              >
                {loadingPaymentId === payment.id ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Загрузка...
                  </>
                ) : (
                  <>
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Оплатить сейчас
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
